/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import PropTypes from 'prop-types'
import { css, jsx } from '@emotion/core'
import {
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  TextField,
  withStyles
} from '@material-ui/core'
import * as DateFns from 'date-fns'
import _ from 'lodash'
import moment from 'moment/dist/moment'
import { toast } from 'react-toastify'
import { DefaultCurrencyCode, theme } from '../../../constants'
import { ProductNames2026 } from '../../../constants/payments'
import orderService from '../../../services/order'
import * as Actions from '../../admin/actions'
import Checkout from '../../components/Checkout'
import RecurrentProduct from '../../components/RecurrentProduct'
import sleep from '../../../utils/sleep'
import { history } from '../../App'
import localizePriceByCountry from '../../../utils/localizePriceByCountry'
import formatPrice from '../../../utils/formatPrice'
import localizeCurrency from '../../../utils/localizeCurrency'
import formatDate from '../../../utils/formatDate'

const styles = {}
styles.container = css`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  margin: auto;
`
styles.loadingContainer = css`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: white;
  opacity: 0.5;
  z-index: 1;
`
styles.header = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  padding: 16px 0px;
  border-top: 1px solid ${theme.palette.common.charolBlack}80;
  border-bottom: 1px solid ${theme.palette.common.charolBlack}80;
`
styles.headerContent = css`
  width: 100%;
  display: flex;
  justify-content: space-between;
`
styles.migrationContainer = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  margin-bottom: 12px;
  background: #ff6467;
`
styles.migrationMessage = css`
  font-size: 13px;

  & a {
    color: white;
    font-weight: bold;
  }
`
styles.title = css`
  font-size: 16px;
  font-weight: bold;
`
styles.description = css`
  font-size: 14px;
`
styles.deprecated = css`
  font-size: 12px;
  font-style: italic;
`
styles.content = css`
  display: flex;
  padding-top: 24px;
`
styles.dateContainer = css`
  display: flex;
  flex-direction: column;
`
styles.actionsContainer = css`
  display: flex;
`
styles.buttonContainer = css`
  margin-left: 16px;
`
styles.newProductButtonContainer = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`
styles.productsContainer = css`
  width: 700px;
  display: flex;
  flex-direction: column;
  margin-right: 32px;
`
styles.checkoutContainer = css`
  width: 400px;
  height: fit-content;
  display: flex;
`
styles.checkoutPlanContainer = css`
  display: flex;
  flex-direction: column;
`
styles.checkoutPlanTitle = css`
  font-size: 16px;
  font-weight: bold;
`
styles.checkoutPaymentContainer = css`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  margin-bottom: 16px;
`
styles.checkoutTotalContainer = css`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`
styles.checkoutTotalText = css`
  font-size: 16px;
  font-weight: bold;
`
styles.vatText = css`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`
styles.checkoutButtonContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0px;
  padding-bottom: 0px;
`
styles.actionButton = {
  fontSize: '12px',
  textTransform: 'none',
  color: 'white',
  borderColor: `${theme.palette.common.offWhite}`
}
styles.newProductButton = {
  color: theme.palette.common.charolBlack,
  textTransform: 'none'
}
styles.checkoutButton = {
  height: '32px',
  color: theme.palette.common.white,
  backgroundColor: theme.palette.common.blue,
  textTransform: 'none',
  borderRadius: '2px',
  padding: '0px 6px'
}

const CssProductSelect = withStyles({
  root: {
    '& .MuiOutlinedInput-input': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      backgroundColor: theme.palette.primary.main,
      padding: '0px 32px 0px 12px',
      height: '32px',
      borderRadius: '4px',
      color: 'white'
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.common.charolBlack
      }
    }
  }
})(TextField)

const devToProdMapping = {
  // 2026 plans.
  pri_01jn15f0jn8bn7qp0gkqccr72x: 'pri_01jn40tkgxvrak0yrzptjxvb3x', // MS_PLAYBASE_ESSENTIALS_MONTHLY_PRICE_ID
  pri_01jn15g05q4xjmxpvs9hasszt2: 'pri_01jn40vfnnc7bmycmczxhdpc98', // MS_PLAYBASE_ESSENTIALS_YEARLY_HALF_PRICE_ID
  pri_01jn15gnrw2jt7hgxr74ght4v9: 'pri_01jn40w19mewxpxkbcct27cnvn', // MS_PLAYBASE_ESSENTIALS_YEARLY_PRICE_ID
  pri_01jn15j2n7295xj3caywasgrkw: 'pri_01jn40xg75pwqk81j1vqe5pacy', // MS_PLAYBASE_PLUS_MONTHLY_PRICE_ID
  pri_01jn15kb5gfamh4mk6hbkyka1y: 'pri_01jn40y52d3eps1b6z6f6xfj11', // MS_PLAYBASE_PLUS_YEARLY_HALF_PRICE_ID
  pri_01jn15krdvg8n7c7xh03hyw570: 'pri_01jn40ymjs2ft1sh9c9s7pk36y', // MS_PLAYBASE_PLUS_YEARLY_PRICE_ID
  pri_01jn15pmqxp77syh4rw6y0bww6: 'pri_01jn4104n5vktmbpgw1axabp41', // MS_PLAYBASE_ADVANCED_MONTHLY_PRICE_ID
  pri_01jn15qhhakb4cydj7q5wjkxgd: 'pri_01jn410tc8a1d93xj7thcbq95h', // MS_PLAYBASE_ADVANCED_YEARLY_HALF_PRICE_ID
  pri_01jn15qyb42p9qar3mm65x4x1e: 'pri_01jn411chp59g3tvwyaaj171ve', // MS_PLAYBASE_ADVANCED_YEARLY_PRICE_ID
  pri_01kh3mcyj3zzcemwh42dfxwa9x: 'pri_01jhfms0t3q5nhq5j064vhaf6h', // MS_NEXUS_PRO_YEARLY_TWO_PRICE_ID
  pri_01kx0g9xnyqhe04ct0xpcaas7w: 'pri_01kx2w7ds3ar1096crdpq7t5nj', // MS_NEXUS_ELITE_MONTHLY_PRICE_ID
  pri_01kx0gb7jt7dnr1zgrkd31yr5r: 'pri_01kx2w92kh0k5qjd4zjwjmpmf9', // MS_NEXUS_ELITE_YEARLY_HALF_PRICE_ID
  pri_01kx0g8mbd0x0ekejw9he81tgd: 'pri_01kx2w9w7ch0afvha60s2pastm', // MS_NEXUS_ELITE_YEARLY_PRICE_ID
  pri_01kx0gck4jhj2xwxaakvgankmj: 'pri_01kx2waf376s56th0pnv4a9td6', // MS_NEXUS_ELITE_YEARLY_TWO_PRICE_ID

  // 2025 plans.
  pri_01j0nmva3yzx8kt7gnzee394dm: 'pri_01j19yy1pz5yyrzkp0sr3njzf9', // MS_NEXUS_ELITE_2025_YEARLY_PRICE_ID

  // 2024 plans.
  pri_01j0nm6zsrgj5cmacxe6p7fmj8: 'pri_01j19yg51sx1eevrzwfhj6e9cy', // MS_STARTER_PAUSE_MONTHLY_PRICE_ID
  pri_01j3j7fybtsbfzzgqnsfxd9pyp: 'pri_01j3fktkt63fvag2qvz1m6jdzd', // MS_STARTER_PAUSE_YEARLY_HALF_PRICE_ID
  pri_01j0nm9dfj4g9e4jp7h3cggryb: 'pri_01j19ygj649z3sdemfx8kvs6xt', // MS_STARTER_PAUSE_YEARLY_PRICE_ID
  pri_01j0nmaqv3edkr4x7zdpzqn5de: 'pri_01j19yhbzb7k6gxwq1wy379kh7', // MS_STARTER_CODE_MONTHLY_PRICE_ID
  pri_01j3j7greq00473zd38mmb4nsq: 'pri_01j3fkvnghz33mnkrkysz9e81s', // MS_STARTER_CODE_YEARLY_HALF_PRICE_ID
  pri_01j0nmb7f6d8vtgx32jnd222v8: 'pri_01j19yhrwzcxmydvrfvqrfwsmr', // MS_STARTER_CODE_YEARLY_PRICE_ID
  pri_01j0nmc1x1vagj6xc96mrmch5b: 'pri_01j19yjn36acssebphymj19r72', // MS_STARTER_CODE_PAUSE_MONTHLY_PRICE_ID
  pri_01j3j7hn6de9j5a9amvddxmw99: 'pri_01j3fkwec8ezrxyz408gw31ys2', // MS_STARTER_CODE_PAUSE_YEARLY_HALF_PRICE_ID
  pri_01j0nmch8ryedfp70bcbg9e4ja: 'pri_01j19yk8hd2802a6qjf56sg3h8', // MS_STARTER_CODE_PAUSE_YEARLY_PRICE_ID
  pri_01j0nmfw0st7bymezxmtrng3dy: 'pri_01j19ym2eyn9pd8j5kn4amk8ah', // MS_ESSENTIALS_MONTHLY_PRICE_ID
  pri_01j3j7jer63hhxh787zz3kkjyj: 'pri_01j3fm0hz1k8f0tnbgaymr1w8w', // MS_ESSENTIALS_YEARLY_HALF_PRICE_ID
  pri_01j0nmga4dx071gnqnf8em8yj2: 'pri_01j19ymcpszn0fz7ffbrbts26y', // MS_ESSENTIALS_YEARLY_PRICE_ID
  pri_01j0nmh543vyakbwvebggsxdnf: 'pri_01j19yn3a7jwye3a9c91m83pjh', // MS_ESSENTIALS_CODE_MONTHLY_PRICE_ID
  pri_01j3j7k45adb4z3jaqjqcrb642: 'pri_01j3fm1pvkmypr6srxwbcvc1vh', // MS_ESSENTIALS_CODE_YEARLY_HALF_PRICE_ID
  pri_01j0nmhq5rs4m75rf0gxp11191: 'pri_01j19ynf184934r61g259d7vgz', // MS_ESSENTIALS_CODE_YEARLY_PRICE_ID
  pri_01j0nmjjah1467pjpbvnydvy8h: 'pri_01j19yp4q09dgvjx7q81w6dt8h', // MS_ESSENTIALS_MFT_MONTHLY_PRICE_ID
  pri_01j3j7kq3tyc28atm8fgt69my7: 'pri_01j3fm2fbaxrvkk058e1rng9hf', // MS_ESSENTIALS_MFT_YEARLY_HALF_PRICE_ID
  pri_01j0nmk5m6x8dkx1vg8c1bv99c: 'pri_01j19ypkpm2ftx3sf5xf3aj6re', // MS_ESSENTIALS_MFT_YEARLY_PRICE_ID
  pri_01j0nmkwj3sx0wpqq7ks601aac: 'pri_01j19yqdk1z96x64v5nxdrc9pz', // MS_ESSENTIALS_MFT_CODE_MONTHLY_PRICE_ID
  pri_01j3j7mcgmffvbzgt2px971mhy: 'pri_01j3fm34agnx5xf5c32av0gghj', // MS_ESSENTIALS_MFT_CODE_YEARLY_HALF_PRICE_ID
  pri_01j0nmmb2xb2b4c8qxz3jabfws: 'pri_01j19yqtpd15jkbr6jzykjs1qq', // MS_ESSENTIALS_MFT_CODE_YEARLY_PRICE_ID
  pri_01j0nmn23gqhwa6py7vr292tnp: 'pri_01j19yrf4xbqcpzz9e1y8r9jbm', // MS_PRIME_MONTHLY_PRICE_ID
  pri_01j3j7myswx7r51mazfe77z7j2: 'pri_01j3fm3rks15s8k5g96t53c2kw', // MS_PRIME_YEARLY_HALF_PRICE_ID
  pri_01j0nmnh8y2pxdxgee7zj7w1r5: 'pri_01j19yrs22q7b9jy4ptpy7rtpk', // MS_PRIME_YEARLY_PRICE_ID
  pri_01j0nmp9hwf1x4j9swbrrw7npd: 'pri_01j19ysnar2yhv883x425ag4ab', // MS_PRIME_MFT_MONTHLY_PRICE_ID
  pri_01j3j7ne6byy2xg4d1xxqqmr42: 'pri_01j3fm4tc5ewj6bf59xwtqshb1', // MS_PRIME_MFT_YEARLY_HALF_PRICE_ID
  pri_01j0nmpp3n9s4hfa898b59zz2r: 'pri_01j19yt0136pbs90dk8pm7vr66', // MS_PRIME_MFT_YEARLY_PRICE_ID
  pri_01j0nmqeww2tt48cvw92q9696p: 'pri_01j19ythacvzwzn7hhcdejz8b8', // MS_PRIME_PRESETS_MONTHLY_PRICE_ID
  pri_01j3j7p0p0mz9qa4ac4hsw814w: 'pri_01j3fm5ek4z232fanx1fpv3azw', // MS_PRIME_PRESETS_YEARLY_HALF_PRICE_ID
  pri_01j0nmqw8pyg0rzpxx56kvq830: 'pri_01j19yty7eckmjqhfe0yt2wszd', // MS_PRIME_PRESETS_YEARLY_PRICE_ID
  pri_01jbchax443fnk5wbw3d4eaf87: 'pri_01jby9nsvr80w06r7q2rt6w59w', // MS_PRIME_LIVE_MONTHLY_PRICE_ID
  pri_01jn17jn826gkaarfjjw6svf6c: 'pri_01jby9qmgrwsp1xwmpwmzma4sd', // MS_PRIME_LIVE_YEARLY_HALF_PRICE_ID
  pri_01jbchbfbnh2w4t9y8td997e4c: 'pri_01jby9p8ra0t635ej4as9kstc4', // MS_PRIME_LIVE_YEARLY_PRICE_ID
  pri_01j0nmrfbjb7ywew5gf21b2zg3: 'pri_01j19yvj2n2g5kh06zwxrb0b19', // MS_PRIME_PRESETS_MFT_MONTHLY_PRICE_ID
  pri_01j3j7phrsrm3e98x0ff7mfpsa: 'pri_01j3fm617xn74nvb34dwg77fdq', // MS_PRIME_PRESETS_MFT_YEARLY_HALF_PRICE_ID
  pri_01j0nmrwmvwr4rvwyxc9bg1efs: 'pri_01j19yvxxqyecshp7ajxsasmjg', // MS_PRIME_PRESETS_MFT_YEARLY_PRICE_ID
  pri_01jbch9hda1y9c47rvkjmtzzjb: 'pri_01jby9tfsc9p3pa0ac3zyfrzrr', // MS_PRIME_PRESETS_LIVE_MONTHLY_PRICE_ID
  empty2: 'pri_01jby9vtw0832r1va1ehpbb2mk', // MS_PRIME_PRESETS_LIVE_YEARLY_HALF_PRICE_ID
  pri_01jbcha7nff20ktge2bn2arx8y: 'pri_01jby9v13t1zkp6ddg26b5fkqn', // MS_PRIME_PRESETS_LIVE_YEARLY_PRICE_ID
  pri_01jbch802phpe7cvnnfcvaf1dh: 'pri_01jby9rg541k05bgj434q9vgkg', // MS_PRIME_MFT_LIVE_MONTHLY_PRICE_ID
  empty3: 'pri_01jby9svads2hqetrn75vmk98n', // MS_PRIME_MFT_LIVE_YEARLY_HALF_PRICE_ID
  pri_01jbch8mpz624ttfmmy6dzg1vj: 'pri_01jby9s043nzct2nc1kpppyh4p', // MS_PRIME_MFT_LIVE_YEARLY_PRICE_ID
  pri_01jbvxc7sqfs2rhwc1e04e2mwy: 'pri_01jby9wjb6dtz6bwk1vwhk2djg', // MS_PRIME_PRESETS_MFT_LIVE_MONTHLY_PRICE_ID
  empty4: 'pri_01jby9xwvr1ncf8htascnsthn3', // MS_PRIME_PRESETS_MFT_LIVE_YEARLY_HALF_PRICE_ID
  pri_01jbvxcm0r5bgw21m8gxcwm968: 'pri_01jby9x32p1f9dqxx1a02hgn8a', // MS_PRIME_PRESETS_MFT_LIVE_YEARLY_PRICE_ID
  pri_01j0nmsspgn6a4rge72fw15mwc: 'pri_01j19ywzgmfrfr1eytqw8vxqyr', // MS_PRO_2024_MONTHLY_PRICE_ID
  pri_01j3j7q22tb4enekab6z9v7mzc: 'pri_01j3fm6sjehn3x6qhr9r6s9s48', // MS_PRO_2024_YEARLY_HALF_PRICE_ID
  pri_01j0nmt64509jc1wmxdbpk0jmw: 'pri_01j19yxb5srmjvqxkea731h8b7', // MS_PRO_2024_YEARLY_PRICE_ID
  pri_01jbchc0nv13mejsa738nf3g10: 'pri_01jby9yw3e6nfg15zb3s9m0fqh', // MS_PRO_2024_LIVE_MONTHLY_PRICE_ID
  pri_01jn17hb9d683ye6zqjaxm3fsy: 'pri_01jby9zx5ad700kz6h43d299pv', // MS_PRO_2024_LIVE_YEARLY_HALF_PRICE_ID
  pri_01jbchcgf6jed8gypg2kfmezvz: 'pri_01jby9zbdxcjfpnryhhqkx7zhy', // MS_PRO_2024_LIVE_YEARLY_PRICE_ID

  // 2023 plans.
  pri_01hr4aweb662ttnzm3nd2mshdw: 'pri_01hqtt5nyp1tz6gs4h59h04dq8', // MS_STUDENT_YEARLY_HALF_PRICE_ID
  pri_01hr4avqv4fek12x0shj1d4hmv: 'pri_01hqt9mb174k9qa2f9ka7kzy2k', // MS_STUDENT_YEARLY_PRICE_ID
  pri_01h2wavy2ysn9k2rg9m4qavmd6: 'pri_01hkz5ppemknkhph6wmze905kd', // MS_STARTER_CODEPAD_MONTHLY_PRICE_ID
  pri_01hprtwm3gy2wszkygcbd0sm68: 'pri_01hpree9favppepe50c7tvc57q', // MS_STARTER_CODEPAD_YEARLY_HALF_PRICE_ID
  pri_01hprtxar1fh923xfsh6es9zx4: 'pri_01hph05ny5kw0eyxydaeqms277', // MS_STARTER_CODEPAD_YEARLY_PRICE_ID
  pri_01h2waxvja5mrm605jc7g5ybby: 'pri_01hkz5qztj3cd0y6rrpgnbpwpy', // MS_BASIC_MONTHLY_PRICE_ID
  pri_01hprtyeqffywm07mgqm59n59e: 'pri_01hprehzaha6k7b5mb2cqt5sq4', // MS_BASIC_YEARLY_HALF_PRICE_ID
  pri_01h2wbfvysgfg35dm67yrc7850: 'pri_01hkz5rw0jhks6hzk7nm0nnbpq', // MS_BASIC_YEARLY_PRICE_ID
  pri_01h2wb08m9cs6qmjsjq4g8g51g: 'pri_01hkz5xdq9kzjzr1yzsnqpmpae', // MS_BASIC_CODEPAD_MONTHLY_PRICE_ID
  pri_01hprv08dhb0mg17nbw876j985: 'pri_01hpreknqay7hwyqz1bqescgyp', // MS_BASIC_CODEPAD_YEARLY_HALF_PRICE_ID
  pri_01h2wbje0c8vdks47pvnkdhrjd: 'pri_01hkz5xw7j1yvh8sdvsq0scmwv', // MS_BASIC_CODEPAD_YEARLY_PRICE_ID
  pri_01h2wb22vjmw209ve5wg22pdgj: 'pri_01hkz5z2jwytzvp6eh31vn62sr', // MS_BASIC_MFT_MONTHLY_PRICE_ID
  pri_01hprv1dehgn1tdgzk372dbcya: 'pri_01hprenzc49sp7qa1brsms19w9', // MS_BASIC_MFT_YEARLY_HALF_PRICE_ID
  pri_01h2wb978z7dncnjmkze6qe725: 'pri_01hkz5zf0e9cqddb4nmnvhvf6p', // MS_BASIC_MFT_YEARLY_PRICE_ID
  pri_01h2wbmwga5j0qaevnkwmvk74y: 'pri_01hkz60a328ky690s332j500j9', // MS_BASIC_MFT_CODEPAD_MONTHLY_PRICE_ID
  pri_01hprv2qrppae75b76j5dmfkbr: 'pri_01hpreqd4a8vwmp5mmwtn3j4kh', // MS_BASIC_MFT_CODEPAD_YEARLY_HALF_PRICE_ID
  pri_01h2wbrxncfwa2kvbqc9w1288f: 'pri_01hkz60re2575b0pxrcr38rhd3', // MS_BASIC_MFT_CODEPAD_YEARLY_PRICE_ID
  pri_01h2wbv55e5591vp9898dkxrtd: 'pri_01hkz61f48cy7s2kzhbm6cefte', // MS_LITE_MONTHLY_PRICE_ID
  pri_01hprv3bwnj9qap4tfbv8b5nn8: 'pri_01hnwvfy21tvfj9c1kq6xgbg5d', // MS_LITE_YEARLY_HALF_PRICE_ID
  pri_01h2wbxx60wy1mtj202ng10vyj: 'pri_01hkz61vgr2e7jryvqxp3z93x6', // MS_LITE_YEARLY_PRICE_ID
  pri_01h2wc09vrwmdqacrt1gm5yr0d: 'pri_01hkz64c6a2n5tajx1zfmdvm0z', // MS_PRO_YEARLY_PRICE_ID
  empty6: 'pri_01hkz664tcts11bhftx4qgf63r', // MS_ELITE_YEARLY_PRICE_ID
  pri_01h2wc7wafxjj9zzk1fs24pw5c: 'pri_01hkz670cjf07h0mm1s9fqqqnn', // MS_MINUTES_10_PRICE_ID
  pri_01h2wc8kav26kt1984c52yb8vv: 'pri_01hkz67bca7p24s1nvkgwpqac9', // MS_MINUTES_100_PRICE_ID
  pri_01h2wc9khxqkbahbd48tfhbvn1: 'pri_01hkz67psn1asx5tb698n7jvv7', // MS_MINUTES_1000_PRICE_ID
  pri_01h2wcb3cq8vcynbppng0zgebn: 'pri_01hkz684d6vn7jys5j19afkcxt' // MS_MINUTES_10000_PRICE_ID
}

const oldToNewProdMapping = {
  // 2025 plans.
  pri_01j19yy1pz5yyrzkp0sr3njzf9: 'pri_01kx2w9w7ch0afvha60s2pastm', // Nexus Elite Yearly => Nexus Elite Yearly

  // 2024 plans.
  pri_01j19yg51sx1eevrzwfhj6e9cy: 'pri_01jn40tkgxvrak0yrzptjxvb3x', // Starter+Pause Monthly => PlayBase Essentials Monthly
  pri_01j19ygj649z3sdemfx8kvs6xt: 'pri_01jn40w19mewxpxkbcct27cnvn', // Starter+Pause Yearly => PlayBase Essentials Yearly
  pri_01j19yhbzb7k6gxwq1wy379kh7: 'pri_01jn40tkgxvrak0yrzptjxvb3x', // Starter+Code Monthly => PlayBase Essentials Monthly
  pri_01j19yhrwzcxmydvrfvqrfwsmr: 'pri_01jn40w19mewxpxkbcct27cnvn', // Starter+Code Yearly => PlayBase Essentials Yearly
  pri_01j19yjn36acssebphymj19r72: 'pri_01jn40tkgxvrak0yrzptjxvb3x', // Full Starter Monthly => PlayBase Essentials Monthly
  pri_01j19yk8hd2802a6qjf56sg3h8: 'pri_01jn40w19mewxpxkbcct27cnvn', // Full Starter Yearly => PlayBase Essentials Yearly
  pri_01j19ym2eyn9pd8j5kn4amk8ah: 'pri_01jn40xg75pwqk81j1vqe5pacy', // Essentials Monthly => PlayBase Plus Monthly
  pri_01j3fm0hz1k8f0tnbgaymr1w8w: 'pri_01jn40y52d3eps1b6z6f6xfj11', // Essentials Half-Yearly => PlayBase Plus Half-Yearly
  pri_01j19ymcpszn0fz7ffbrbts26y: 'pri_01jn40ymjs2ft1sh9c9s7pk36y', // Essentials Yearly => PlayBase Plus Yearly
  pri_01j19yn3a7jwye3a9c91m83pjh: 'pri_01jn40xg75pwqk81j1vqe5pacy', // Essentials+Code Monthly => PlayBase Plus Monthly
  pri_01j3fm1pvkmypr6srxwbcvc1vh: 'pri_01jn40y52d3eps1b6z6f6xfj11', // Essentials+Code Half-Yearly => PlayBase Plus Half-Yearly
  pri_01j19ynf184934r61g259d7vgz: 'pri_01jn40ymjs2ft1sh9c9s7pk36y', // Essentials+Code Yearly => PlayBase Plus Yearly
  pri_01j19yp4q09dgvjx7q81w6dt8h: 'pri_01jn40xg75pwqk81j1vqe5pacy', // Essentials+MFT Monthly => PlayBase Plus Monthly
  pri_01j19ypkpm2ftx3sf5xf3aj6re: 'pri_01jn40ymjs2ft1sh9c9s7pk36y', // Essentials+MFT Yearly => PlayBase Plus Yearly
  pri_01j19yqdk1z96x64v5nxdrc9pz: 'pri_01jn4104n5vktmbpgw1axabp41', // Full Essentials Monthly => PlayBase Advanced Monthly
  pri_01j19yqtpd15jkbr6jzykjs1qq: 'pri_01jn411chp59g3tvwyaaj171ve', // Full Essentials Yearly => PlayBase Advanced Yearly
  pri_01j19ysnar2yhv883x425ag4ab: 'pri_01j19yrf4xbqcpzz9e1y8r9jbm', // Prime+MFT Monthly => Nexus Prime Monthly
  pri_01j19yt0136pbs90dk8pm7vr66: 'pri_01j19yrs22q7b9jy4ptpy7rtpk', // Prime+MFT Yearly => Nexus Prime Yearly
  pri_01jby9rg541k05bgj434q9vgkg: 'pri_01jby9wjb6dtz6bwk1vwhk2djg', // Prime+MFT+Live Monthly => Nexus Prime+Live Monthly
  pri_01jby9s043nzct2nc1kpppyh4p: 'pri_01jby9x32p1f9dqxx1a02hgn8a', // Prime+MFT+Live Yearly => Nexus Prime+Live Yearly
  pri_01j19ythacvzwzn7hhcdejz8b8: 'pri_01j19yrf4xbqcpzz9e1y8r9jbm', // Prime+Presets Monthly => Nexus Prime Monthly
  pri_01j19yty7eckmjqhfe0yt2wszd: 'pri_01j19yrs22q7b9jy4ptpy7rtpk', // Prime+Presets Yearly => Nexus Prime Yearly
  pri_01jby9tfsc9p3pa0ac3zyfrzrr: 'pri_01jby9wjb6dtz6bwk1vwhk2djg', // Prime+Presets+Live Monthly => Nexus Prime+Live Monthly
  pri_01jby9v13t1zkp6ddg26b5fkqn: 'pri_01jby9x32p1f9dqxx1a02hgn8a', // Prime+Presets+Live Yearly => Nexus Prime+Live Yearly
  pri_01j19yvj2n2g5kh06zwxrb0b19: 'pri_01j19ywzgmfrfr1eytqw8vxqyr', // Prime+MFT+Presets Monthly => Nexus Pro Monthly
  pri_01j19yvxxqyecshp7ajxsasmjg: 'pri_01j19yxb5srmjvqxkea731h8b7', // Prime+MFT+Presets Yearly => Nexus Pro Yearly
  pri_01jby9wjb6dtz6bwk1vwhk2djg: 'pri_01jby9yw3e6nfg15zb3s9m0fqh', // Full Prime Monthly => Nexus Pro+Live Monthly
  pri_01jby9x32p1f9dqxx1a02hgn8a: 'pri_01jby9zbdxcjfpnryhhqkx7zhy', // Full Prime Yearly => Nexus Pro+Live Yearly

  // 2023 plans.
  pri_01hkz5ppemknkhph6wmze905kd: 'pri_01jn40tkgxvrak0yrzptjxvb3x', // Code Monthly => PlayBase Essentials Monthly
  pri_01hph05ny5kw0eyxydaeqms277: 'pri_01jn40w19mewxpxkbcct27cnvn', // Code Yearly => PlayBase Essentials Yearly
  pri_01hkz5qztj3cd0y6rrpgnbpwpy: 'pri_01j19yrf4xbqcpzz9e1y8r9jbm', // Basic Monthly => Nexus Prime Monthly
  pri_01hkz5rw0jhks6hzk7nm0nnbpq: 'pri_01j19yrs22q7b9jy4ptpy7rtpk', // Basic Yearly => Nexus Prime Yearly
  pri_01hkz5xdq9kzjzr1yzsnqpmpae: 'pri_01j19yrf4xbqcpzz9e1y8r9jbm', // Basic+Code Monthly => Nexus Prime Monthly
  pri_01hkz5xw7j1yvh8sdvsq0scmwv: 'pri_01j19yrs22q7b9jy4ptpy7rtpk', // Basic+Code Yearly => Nexus Prime Yearly
  pri_01hkz5z2jwytzvp6eh31vn62sr: 'pri_01j19yrf4xbqcpzz9e1y8r9jbm', // Basic+MFT Monthly => Nexus Prime Monthly
  pri_01hkz5zf0e9cqddb4nmnvhvf6p: 'pri_01j19yrs22q7b9jy4ptpy7rtpk', // Basic+MFT Yearly => Nexus Prime Yearly
  pri_01hkz60a328ky690s332j500j9: 'pri_01j19yrf4xbqcpzz9e1y8r9jbm', // Full Basic Monthly => Nexus Prime Monthly
  pri_01hkz60re2575b0pxrcr38rhd3: 'pri_01j19yrs22q7b9jy4ptpy7rtpk', // Full Basic Yearly => Nexus Prime Yearly
  pri_01hkz61f48cy7s2kzhbm6cefte: 'pri_01j19ywzgmfrfr1eytqw8vxqyr', // Lite Monthly => Nexus Pro Monthly
  pri_01hkz61vgr2e7jryvqxp3z93x6: 'pri_01j19yxb5srmjvqxkea731h8b7', // Lite Yearly => Nexus Pro Yearly
  pri_01hkz64c6a2n5tajx1zfmdvm0z: 'pri_01kx2w9w7ch0afvha60s2pastm' // Pro Yearly => Nexus Elite Yearly
}

function Plan(props) {
  const [dropdownProducts, setDropdownProducts] = React.useState([])
  const [cycle, setCycle] = React.useState('monthly')
  const [countryTaxRate, setCountryTaxRate] = React.useState(0)
  const [countryCurrencyCode, setCountryCurrencyCode] = React.useState('')
  const [countryCode, setCountryCode] = React.useState('')
  const [plansOriginal, setPlansOriginal] = React.useState([])
  const [plansCheckout, setPlansCheckout] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [enableUpdateSubscription, setEnableUpdateSubscription] = React.useState(false)
  const [enableUpdatePaymentMethod, setEnableUpdatePaymentMethod] = React.useState(false)

  React.useEffect(() => {
    if (!props.paddle || props.products.length === 0) {
      return
    }

    const previewRequest = {
      items: [{ priceId: props.products[0].priceId, quantity: 1 }]
    }

    if (props.address && props.address.id) {
      previewRequest.addressId = props.address.id
    } else {
      previewRequest.customerIpAddress = props.userIp
    }

    if (props.customer && props.customer.id) {
      previewRequest.customerId = props.customer.id
    }

    props.paddle.PricePreview(previewRequest).then(preview => {
      setCountryCode(preview.data.address.countryCode)
      setCountryCurrencyCode(preview.data.currencyCode)
      setCountryTaxRate(Number(preview.data.details.lineItems[0].taxRate) * 100.0)
    })
  }, [props.paddle, props.customer, props.user, props.userIp, props.products])

  React.useEffect(() => {
    if (
      props.monthlyProducts.length > 0 ||
      props.halfYearlyProducts.length > 0 ||
      props.yearlyProducts.length > 0
    ) {
      setLoading(false)
    }
  }, [props.monthlyProducts, props.halfYearlyProducts, props.yearlyProducts])

  React.useEffect(() => {
    if (props.subscription.order) {
      // Read items from order and link them to the products according to current billing cycle.
      let planProducts =
        cycle === 'monthly'
          ? props.monthlyProducts
          : cycle === 'yearly'
            ? props.yearlyProducts
            : props.halfYearlyProducts
      let newPlansCheckout = props.subscription.order.items.reduce((acc, v) => {
        const product = planProducts.find(p => p.priceId === v.id)
        if (product) {
          acc.push({ ...product, quantity: v.quantity })
        }

        return acc
      }, [])

      // If there are no products linked for the current cycle, try the other one.
      if (newPlansCheckout.length === 0) {
        planProducts = cycle !== 'monthly' ? props.monthlyProducts : props.yearlyProducts
        newPlansCheckout = props.subscription.order.items.reduce((acc, v) => {
          const product = planProducts.find(p => p.priceId === v.id)
          if (product) {
            acc.push({ ...product, quantity: v.quantity })
          }

          return acc
        }, [])

        // If there are products in the other cycle, switch to it.
        if (newPlansCheckout.length > 0) {
          setCycle(cycle === 'monthly' ? 'yearly' : 'monthly')
        } else {
          // If there are no products in the other cycle, try the half-yearly one.
          planProducts = props.halfYearlyProducts
          newPlansCheckout = props.subscription.order.items.reduce((acc, v) => {
            const product = planProducts.find(p => p.priceId === v.id)
            if (product) {
              acc.push({ ...product, quantity: v.quantity })
            }

            return acc
          }, [])

          // If there are products in the half-yearly cycle, switch to it.
          if (newPlansCheckout.length > 0) {
            setCycle('half-yearly')
          }
        }
      }

      setPlansOriginal(newPlansCheckout)
      setPlansCheckout(_.cloneDeep(newPlansCheckout))
    }

    updateDropdownProducts(
      cycle === 'monthly'
        ? props.monthlyProducts
        : cycle === 'yearly'
          ? props.yearlyProducts
          : props.halfYearlyProducts
    )
  }, [props.subscription, props.monthlyProducts, props.yearlyProducts])

  React.useEffect(() => {
    const planProducts =
      cycle === 'monthly'
        ? props.monthlyProducts
        : cycle === 'yearly'
          ? props.yearlyProducts
          : props.halfYearlyProducts

    // Replace existing items with the products that are available in the new cycle.
    if (plansCheckout.length > 0) {
      const newPlansCheckout = plansCheckout.reduce((acc, v) => {
        const product = planProducts.find(p => p.productCode === v.productCode)
        if (product && product.priceId) {
          acc.unshift({ ...product, quantity: v.quantity })
        }

        return acc
      }, [])
      setPlansCheckout(newPlansCheckout)
    }

    updateDropdownProducts(planProducts)
  }, [cycle])

  React.useEffect(() => {
    return setEnableUpdatePaymentMethod(
      props.subscription.order && props.subscription.order.status === 'past_due'
    )
  }, [props.subscription])

  React.useEffect(() => {
    return setEnableUpdateSubscription(
      _.differenceWith(plansCheckout, plansOriginal, _.isEqual).length !== 0 ||
        _.differenceWith(plansOriginal, plansCheckout, _.isEqual).length !== 0
    )
  }, [plansCheckout, plansOriginal])

  function updateDropdownProducts(newProducts) {
    const newDropdownProducts = newProducts
      .filter(v => !v.isDeprecated && !v.isPrivate)
      .map(v => {
        const product = ProductNames2026.find(w => w.value === v.productCode)

        return {
          value: v.priceId,
          label: window.I18n.t('plans.plan', {
            planName: product.translationKey
              ? window.I18n.t(`plans.${product.translationKey}`)
              : product.translation
          })
        }
      })
    newDropdownProducts.unshift({
      value: ProductNames2026[0].value,
      label: window.I18n.t(`plans.${ProductNames2026[0].translationKey}`)
    })
    setDropdownProducts(newDropdownProducts)
  }

  function handleSelectProduct(e) {
    const productId = e.target.value
    const product = props.products.find(v => v.priceId === productId)
    if (!product) {
      return
    }

    const itemIndex = plansCheckout.findIndex(v => v.priceId === product.priceId)
    if (itemIndex !== -1) {
      const newPlansCheckout = [...plansCheckout]
      newPlansCheckout[itemIndex].quantity = Math.min(99, newPlansCheckout[itemIndex].quantity + 1)
      setPlansCheckout(newPlansCheckout)
    } else {
      // If there's only one deprecated product, replace it with the new one.
      const newPlansCheckout = [{ ...product, quantity: 1 }]
      if (plansCheckout.length === 1 && plansCheckout[0].isDeprecated) {
        newPlansCheckout[0].quantity = plansCheckout[0].quantity
      } else {
        newPlansCheckout.push(...plansCheckout)
      }

      setPlansCheckout(newPlansCheckout)
    }
  }

  function handleUpgradePlan(upgradedPlan, plan) {
    const newPlansCheckout = [...plansCheckout]

    const existingPlan = newPlansCheckout.find(v => v.productCode === upgradedPlan.productCode)

    if (existingPlan) {
      upgradedPlan.quantity = existingPlan.quantity + 1
    }

    if (plan) {
      handleUpdatePlans([upgradedPlan, plan])
    } else {
      handleUpdatePlans([upgradedPlan])
    }
  }

  function handleUpdatePlans(plans) {
    const newPlansCheckout = [...plansCheckout]

    let lastIndex = plans.length - 1

    plans.forEach(product => {
      const index = newPlansCheckout.findIndex(v => v.priceId === product.priceId)
      if (index === -1) {
        newPlansCheckout.splice(lastIndex, 0, product)
      } else {
        newPlansCheckout[index] = product
        lastIndex = index
      }
    })

    let newPlansCheckoutWithQuantity = newPlansCheckout.filter(v => v.quantity > 0)
    if (newPlansCheckoutWithQuantity.length === 0) {
      newPlansCheckoutWithQuantity = _.cloneDeep(plansOriginal)
    }

    let newCycle = null

    // TODO: Handle cases where two plans have different cycles and
    // are only available in their respective billing cycles.
    newPlansCheckoutWithQuantity.forEach(v => {
      const parsedCycle = v.cycle
      const otherBillingCycleProducts =
        parsedCycle === 'monthly' ? props.yearlyProducts : props.monthlyProducts

      const planIsOnlyAvailableInItsBillingCycle = !otherBillingCycleProducts.find(
        j => j.productCode === v.productCode
      )

      if (planIsOnlyAvailableInItsBillingCycle && parsedCycle !== cycle) {
        newCycle = parsedCycle
      }
    })

    if (newCycle) {
      setCycle(newCycle)
    }

    setPlansCheckout(newPlansCheckoutWithQuantity)
  }

  async function handlePaymentMethod() {
    const response = await orderService.updatePaymentMethodSubscription(
      props.subscription.order.code
    )

    props.paddle.Checkout.open({
      settings: {
        allowLogout: false,
        locale: props.user.language,
        theme: 'dark'
      },
      transactionId: response.data.id,
      customData: window.MetricaSportsAnalytics.getSourceInfo()
    })
  }

  async function handleCheckout() {
    // If user doesn't have a subscription, create it opening Paddle Checkout to fill card details.
    if (props.subscription.order) {
      try {
        setLoading(true)

        const items = plansCheckout.map(v => ({ id: v.priceId, quantity: v.quantity }))
        const response = await orderService.updateSubscription(props.subscription.order.code, items)
        const subscription = response.data
        if (subscription) {
          await sleep(5000)
          await Actions.updateUser()

          toast.info(window.I18n.t('subscription.updatedSuccessfully'))
        } else {
          throw new Error(window.I18n.t('subscription.pendingRefundRequest'))
        }
      } catch (e) {
        toast.error(window.I18n.t('subscription.errorUpdating'))
      } finally {
        await Actions.updateUser()
        setLoading(false)
      }
    } else {
      const items = plansCheckout.map(v => ({ priceId: v.priceId, quantity: v.quantity }))
      const checkoutSettings = {
        settings: {
          allowLogout: false,
          locale: props.user.language,
          theme: 'dark'
        },
        customer: {},
        customData: window.MetricaSportsAnalytics.getSourceInfo(),
        items
      }

      if (props.customer && props.customer.id) {
        checkoutSettings.customer.id = props.customer.id
      } else {
        checkoutSettings.customer.email = props.user.email
      }

      props.paddle.Checkout.open(checkoutSettings)
    }
  }

  const showUndoButton =
    props.subscription.order &&
    !props.subscription.order.renewal_at &&
    (props.subscription.order.cancel_at || props.subscription.order.pause_at)
  const showPauseOrCancelButton = !showUndoButton

  let buttonText = window.I18n.t('checkout.proceed')
  let hintText = window.I18n.t('checkout.discountCodes')

  if (props.subscription.order) {
    if (!enableUpdatePaymentMethod) {
      buttonText = window.I18n.t('checkout.updateSubscription')
      hintText = ''
    } else {
      buttonText = window.I18n.t('checkout.updatePaymentMethod')
      hintText = window.I18n.t('checkout.unpaid')
    }
  }

  const currencyCode = props.subscription.order
    ? props.subscription.order.currency_code || DefaultCurrencyCode
    : countryCurrencyCode

  const currentPlans =
    plansCheckout.length > 0
      ? plansCheckout
      : props.subscription.order
        ? props.subscription.order.items
        : []
  const currentPlan = _.maxBy(currentPlans, 'quantity')
  const needsMigration =
    (plansCheckout.some(v => v.isDeprecated) || plansCheckout.length === 0) &&
    props.subscription.order &&
    props.subscription.order.renewal_at
  let newPlanToMigrate
  let newPrice
  let cycleTranslation
  let migrationDate
  if (needsMigration && currentPlan) {
    cycleTranslation =
      cycle === 'monthly'
        ? window.I18n.t('checkout.month')
        : cycle === 'yearly'
          ? window.I18n.t('checkout.year')
          : window.I18n.t('checkout.halfYear')

    let prodPriceId = currentPlan.priceId || currentPlan.id
    const isProd = process.env.REACT_APP_ENV === 'production'
    if (!isProd) {
      prodPriceId = devToProdMapping[prodPriceId]
    }

    if (prodPriceId) {
      let newPriceIdToMigrate = oldToNewProdMapping[prodPriceId]
      if (!isProd) {
        newPriceIdToMigrate = Object.keys(devToProdMapping).find(v => {
          return devToProdMapping[v] === newPriceIdToMigrate
        })
      }

      newPlanToMigrate = props.products.find(v => v.priceId === newPriceIdToMigrate)
      if (newPlanToMigrate) {
        newPrice = `${localizeCurrency(newPlanToMigrate, countryCode)} ${formatPrice(
          localizePriceByCountry(newPlanToMigrate, currencyCode, countryCode),
          currencyCode
        )}`
        migrationDate = formatDate(props.subscription.order.renewal_at)
        // If migration-date is less than 1st April, compute next renewal date taking into account period.
        if (new Date(migrationDate) < new Date('2025-04-01')) {
          const nextRenewalDate = DateFns.addMonths(
            new Date(migrationDate),
            cycle === 'monthly' ? 1 : cycle === 'yearly' ? 12 : 6
          )
          migrationDate = formatDate(nextRenewalDate)
        }
      }
    }
  }

  return (
    <div css={styles.container}>
      {loading && (
        <div css={styles.loadingContainer}>
          <CircularProgress size={32} thickness={4} />
        </div>
      )}
      <div css={styles.header} style={{ pointerEvents: loading ? 'none' : 'inherit' }}>
        {/* Migration warning. TODO: remove after March 2026. */}
        {newPlanToMigrate && (
          <div css={styles.migrationContainer}>
            <span
              css={styles.migrationMessage}
              dangerouslySetInnerHTML={{
                __html: window.I18n.t('subscription.planMigrationMessage', {
                  date: migrationDate,
                  currentPlan: props.subscription.planName,
                  newPlan: newPlanToMigrate.name,
                  newPrice,
                  cycle: cycleTranslation,
                  link: newPlanToMigrate.name.toLowerCase().includes('nexus')
                    ? 'https://www.metrica-sports.com/metrica-nexus#pricing-nexus'
                    : 'https://www.metrica-sports.com/metrica-playbase#pricing-playbase'
                })
              }}
            />
          </div>
        )}

        <div css={styles.headerContent}>
          <div css={styles.dateContainer}>
            <span css={styles.title}>{window.I18n.t('subscription.yourSubscription')}</span>
            <span css={styles.description}>
              {!props.subscription.order && <span>{window.I18n.t('subscription.free')}</span>}

              {props.subscription.order && props.subscription.order.pause_at && (
                <span>
                  <span css={styles.infoPlanDescription}>{window.I18n.t('settings.pauseAt')}</span>
                  <span> </span>
                  <span css={styles.fontBolder}>
                    {formatDate(props.subscription.order.pause_at)}
                  </span>
                </span>
              )}

              {props.subscription.order && props.subscription.order.cancel_at && (
                <span>
                  <span css={styles.infoPlanDescription}>{window.I18n.t('settings.cancelAt')}</span>
                  <span> </span>
                  <span css={styles.fontBolder}>
                    {formatDate(props.subscription.order.cancel_at)}
                  </span>
                </span>
              )}

              {props.subscription.order && showPauseOrCancelButton && (
                <span>
                  <span css={styles.infoPlanDescription}>{window.I18n.t('settings.renewAt')}</span>
                  <span> </span>
                  <span css={styles.fontBolder}>
                    {formatDate(props.subscription.order.renewal_at)}
                  </span>
                </span>
              )}
            </span>

            {plansCheckout.some(v => v.isDeprecated) && (
              <span css={styles.deprecated}>{window.I18n.t('subscription.isObsolete')}</span>
            )}
          </div>

          <div css={styles.actionsContainer}>
            {props.subscription.order && showPauseOrCancelButton && (
              <div css={styles.buttonContainer}>
                <Button
                  variant="outlined"
                  type="submit"
                  style={styles.actionButton}
                  onClick={handlePaymentMethod}
                >
                  {window.I18n.t('subscription.updatePaymentMethod')}
                </Button>
              </div>
            )}

            {props.subscription.order && showPauseOrCancelButton && (
              <div css={styles.buttonContainer}>
                <Button
                  variant="outlined"
                  type="submit"
                  style={styles.actionButton}
                  onClick={async () => {
                    if (!window.profitwell) {
                      setLoading(true)
                      await orderService.cancelSubscription(props.subscription.order.code)
                      await sleep(5000)
                      await Actions.updateUser()
                      toast.info(window.I18n.t('settings.planCanceled'))
                      setLoading(false)

                      return
                    }

                    const result = await window.profitwell('init_cancellation_flow', {
                      user_id: props.subscription.order.customer_id,
                      subscription_id: props.subscription.order.code
                    })

                    // This means the customer accepted a salvage attempt or salvage offer.
                    // Thus, we have to update the user in case he has downgrade the plan.
                    if (result.status === 'retained') {
                      await sleep(5000)
                      await Actions.updateUser()
                      return
                    }

                    // This means the customer either aborted the flow (i.e. they clicked on
                    // "never mind, I don't want to cancel"), or could also be the case the
                    // widget couldn't be shown properly for some reason.
                    if (result.status === 'aborted' || result.status === 'error') {
                      return
                    }

                    // At this point, the customer ended deciding to cancel (i.e.
                    // they rejected the salvage attempts and the salvage offer).
                    setLoading(true)
                    await orderService.cancelSubscription(props.subscription.order.code)
                    await sleep(5000)
                    await Actions.updateUser()
                    toast.info(window.I18n.t('settings.planCanceled'))
                    setLoading(false)
                  }}
                >
                  {window.I18n.t('subscription.cancelSubscription')}
                </Button>
              </div>
            )}

            {props.subscription.order && showUndoButton && (
              <div css={styles.buttonContainer}>
                <Button
                  variant="outlined"
                  type="submit"
                  style={styles.actionButton}
                  onClick={async () => {
                    setLoading(true)
                    await orderService.undoSchedulesSubscription(props.subscription.order.code)
                    await sleep(5000)
                    await Actions.updateUser()
                    toast.info('Subscription cancellation disabled')
                    setLoading(false)
                  }}
                >
                  {window.I18n.t(
                    `subscription.${
                      props.subscription.order.cancel_at ? 'undoCancel' : 'undoPause'
                    }`
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div css={styles.content} style={{ pointerEvents: loading ? 'none' : 'inherit' }}>
        <div css={styles.productsContainer}>
          <div css={styles.newProductButtonContainer}>
            <span css={styles.title}>{window.I18n.t('subscription.yourPlans')}</span>
            <FormControl>
              <CssProductSelect
                select
                variant="outlined"
                name="product"
                value="add"
                disabled={plansCheckout.some(v => v.isPrivate)}
                onChange={handleSelectProduct}
              >
                {dropdownProducts.map(v => {
                  if (!v.value) {
                    return null
                  }

                  return (
                    <MenuItem key={`menu-item-${v.value}`} value={v.value}>
                      <span css={styles.productDropdownLabel}>{v.label}</span>
                    </MenuItem>
                  )
                })}
              </CssProductSelect>
            </FormControl>
          </div>

          {
            // Display free plan.
            (props.monthlyProducts.length > 0 ||
              props.halfYearlyProducts.length > 0 ||
              props.yearlyProducts.length > 0) &&
              plansCheckout.length === 0 && (
                <RecurrentProduct
                  currencyCode={countryCurrencyCode}
                  countryCode={countryCode}
                  cycle={cycle}
                  monthlyProducts={props.monthlyProducts}
                  halfYearlyProducts={props.halfYearlyProducts}
                  yearlyProducts={props.yearlyProducts}
                  onUpgrade={handleUpgradePlan}
                />
              )
          }

          {plansCheckout.map(v => {
            // Don't show private subscriptions.
            if (v.isPrivate) {
              return null
            }

            return (
              <RecurrentProduct
                key={`recurrent-product-${v.id}`}
                product={v}
                // If the subscription exists and currency-code field is empty, it means it's in EUR.
                // Otherwise, use the country's currency-code.
                currencyCode={currencyCode}
                countryCode={countryCode}
                isShopping={false}
                cycle={cycle}
                showRemove={enableUpdateSubscription || plansCheckout.length > 1}
                monthlyProducts={props.monthlyProducts}
                halfYearlyProducts={props.halfYearlyProducts}
                yearlyProducts={props.yearlyProducts}
                onUpdate={handleUpdatePlans}
                onUpgrade={handleUpgradePlan}
              />
            )
          })}
        </div>

        <div css={styles.checkoutContainer}>
          <Checkout
            paddle={props.paddle}
            customer={props.customer}
            address={props.address}
            userIp={props.userIp}
            user={props.user}
            products={props.products}
            buttonDisabled={!enableUpdateSubscription && !enableUpdatePaymentMethod}
            buttonText={buttonText}
            hintText={hintText}
            totalsDisabled={enableUpdatePaymentMethod}
            subscription={props.subscription}
            plansCheckout={plansCheckout}
            // If the subscription exists and currency-code field is empty, it means it's in EUR.
            // Otherwise, use the country's currency-code.
            currencyCode={currencyCode}
            countryCode={countryCode}
            taxRate={
              props.subscription.order && Number.isFinite(props.subscription.order.tax_rate)
                ? props.subscription.order.tax_rate * 100.0
                : countryTaxRate
            }
            cycle={cycle}
            isShopping={false}
            onBillingCycle={setCycle}
            onCheckout={enableUpdatePaymentMethod ? handlePaymentMethod : handleCheckout}
          />
        </div>
      </div>
    </div>
  )
}

Plan.defaultProps = {
  paddle: undefined,
  customer: undefined,
  address: undefined
}

Plan.propTypes = {
  paddle: PropTypes.object,
  products: PropTypes.array.isRequired,
  monthlyProducts: PropTypes.array.isRequired,
  halfYearlyProducts: PropTypes.array.isRequired,
  yearlyProducts: PropTypes.array.isRequired,
  subscription: PropTypes.object.isRequired,
  userIp: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  customer: PropTypes.object,
  address: PropTypes.object
}

export default Plan
